"use client";

import { useEffect, useState, type FormEvent } from "react";
import { useRouter } from "next/navigation";
import * as s from "@/lib/styles";

export default function LoginPage() {
  const router = useRouter();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Forward already-authenticated users to the app. Validated against the
  // backend (not just cookie presence), so a stale/invalid cookie leaves the
  // visitor here to re-login instead of bouncing /login <-> /.
  useEffect(() => {
    fetch("/api/me")
      .then((r) => {
        if (r.ok) router.replace("/");
      })
      .catch(() => {});
  }, [router]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const res = await fetch("/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      if (res.ok) {
        router.push("/");
        router.refresh();
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || "Login failed");
      }
    } catch {
      setError("Network error");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-100px)]">
      <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-6">
        <h1 className="text-2xl font-bold text-cyan text-center">Sign In</h1>
        <div className={s.card}>
          <div className={s.fieldRow}>
            <label className={s.label}>Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className={s.input}
              autoFocus
              autoComplete="username"
            />
          </div>
          <div className={s.fieldRow}>
            <label className={s.label}>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={s.input}
              autoComplete="current-password"
            />
          </div>
          {error && <p className="text-red text-sm">{error}</p>}
        </div>
        <div className="flex justify-end">
          <button type="submit" disabled={submitting} className={s.btnPrimary}>
            {submitting ? "Signing in..." : "Sign In"}
          </button>
        </div>
      </form>
    </div>
  );
}
